import React, { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Carousel,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useResize } from '../../utils/useResize';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: #333333;
`;

const HeaderSection = styled.div`
  background-color: #f3f3f3;
  padding: 23px 78px 29px 28px;
  min-height: 127px;
  ${mobile`
    padding: 16px;
    height: auto;
  `}
`;

const CarouselContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  ${mobile`  
   grid-template-columns: repeat(1, 1fr);
  `}
  column-gap: 13px;
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  height: 100%;
`;

const TextSection = styled.div`
  position: absolute;
  bottom: 0;
  padding: 20px;

  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: left;
  color: ${({ textColor }) => textColor || '#fff'};
`;

const ImgContainer = styled.div`
  padding-top: 11px;
`;

const ImageSection = styled.div`
  flex-basis: 100%;
  padding: 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-around;
  ${mobile`
    flex-direction: column;
  `}
`;

const SelectSizeSection = styled.div`
  flex-basis: 100%;
`;

const TitleSelectSize = styled.h4`
  padding: 0px 10px 10px 0px;
`;

const SizeItemContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  ${mobile`  
    column-gap: 1px;
    row-gap: 3px;
  `}
`;

const Hr = styled.hr`
  height: 90%;
  border: 1px solid #d3d3d3;
`;

const SizeInfoSection = styled.div`
  flex-basis: 50%;
  padding-top: 10px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;

  ${mobile`
    padding-left: 0px;
    display:flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const SizeInfoLine = styled.div`
  min-width: 0;

  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  display: flex;
  flex-direction: row;
  ${mobile`
    display:flex;
    flex-direction: column;
    padding: 10px 10px 10px 0px;
`}
`;

const SizeItem = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 23px;
  letter-spacing: 0em;
  text-align: center;
  vertical-align: middle;
  line-height: 40px;
  color: #000000;
  cursor: default;
`;

const SizeItemBox = styled.div`
  border: 2px solid ${({ isSelected }) => (isSelected ? '#000000;' : '#D3D3D3')};
  flex-basis: 14%;
  width: 80px;
  height: 40px;
  left: 204px;
  top: 721px;
  background: #ffffff;
  border-radius: 4px;
  text-align: center;
  ${mobile`
      flex-basis: 33%;
  `}
`;

const THInfoLine = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  color: #333333;
  padding-right: 10px;
  white-space: nowrap;
  ${mobile`
    white-space: normal;
  `}
`;

const THInfoValue = styled.div`
  white-space: nowrap;
  ${mobile`
    white-space: normal;
  `}
`;

export const BuilderFindYourFitUnit = (props) => {
  const [selected, setSelected] = useState(0);
  const { isMobile } = useBreakpoint(null);
  const { sizes } = props;

  const sizeInfo = useMemo(() => sizes[selected], [selected, sizes]);
  const refreshHash = `${isMobile ? 'mobile' : 'desktop'}`;
  const [flickityRefCallback] = useResize(sizes, refreshHash);

  const groups = isMobile ? 1 : 3;

  const slidesCarousel = sizeInfo?.slides?.reduce(
    (groupSlides, item, index) => {
      const groupIndex = Math.floor(index / groups);

      if (!groupSlides[groupIndex]) {
        groupSlides[groupIndex] = [];
      }

      groupSlides[groupIndex] = [...groupSlides[groupIndex], item];

      return groupSlides;
    },
    []
  );

  return (
    <Container data-builder-component-type="find_your_fit_unit">
      <HeaderSection>
        <HeaderContainer>
          <SelectSizeSection>
            <TitleSelectSize>
              <FormattedMessage
                defaultMessage="SELECT SIZE:"
                id="storytelling_find_your_fit.select_size"
              />
            </TitleSelectSize>
            <SizeItemContainer>
              {sizes.map((sizeItem, i) => (
                <SizeItemBox
                  key={i}
                  isSelected={i === selected}
                  onClick={() => setSelected(i)}
                >
                  <SizeItem>{sizeItem?.size}</SizeItem>
                </SizeItemBox>
              ))}
            </SizeItemContainer>
          </SelectSizeSection>
          <div>
            <Hr />
          </div>
          <SizeInfoSection>
            <SizeInfoLine>
              <THInfoLine>
                <FormattedMessage
                  defaultMessage="SELECTED SIZE:"
                  id="storytelling_find_your_fit.selected_size"
                />
              </THInfoLine>
              <THInfoValue>{sizeInfo?.label}</THInfoValue>
            </SizeInfoLine>
            {sizeInfo?.info?.map(
              ({ title, description }, i) =>
                (title || description) && (
                  <SizeInfoLine key={i}>
                    <THInfoLine>
                      <div>
                        {title?.toUpperCase()}
                        {title && description ? ':' : ''}
                      </div>
                    </THInfoLine>
                    <THInfoValue>{description}</THInfoValue>
                  </SizeInfoLine>
                )
            )}
          </SizeInfoSection>
        </HeaderContainer>
      </HeaderSection>
      <Carousel
        flickityRef={flickityRefCallback}
        prevNextButtons={false}
        pageDots={false}
        imagesLoaded
        resize
      >
        {slidesCarousel?.map((group, i) => {
          return (
            <CarouselContainer key={i}>
              {group.map((slide, i) => {
                return (
                  <Hero key={i}>
                    <TextSection
                      textColor={slide.labelColor || sizeInfo.slideLabelColor}
                    >
                      {slide.label}
                    </TextSection>
                    <ImageSection>
                      <ImgContainer>
                        <img
                          height="100%"
                          width="100%"
                          src={slide.image}
                          key={i}
                          alt=""
                        />
                      </ImgContainer>
                    </ImageSection>
                  </Hero>
                );
              })}
            </CarouselContainer>
          );
        })}
      </Carousel>
    </Container>
  );
};

BuilderFindYourFitUnit.propTypes = {
  sizes: PropTypes.array,
};

export default BuilderFindYourFitUnit;
